import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import API from "@/utils/api";
import RiderLayout from "./RiderLayout";
import { Check, Navigation, MapPin, Truck, ChevronRight, Bike, Car, Zap } from "lucide-react";
import { renderToString } from "react-dom/server";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Leaflet custom icons
const iconPickup = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});
const iconDropoff = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const getVehicleIcon = (vehicle) => {
    const type = vehicle?.toLowerCase() || "";
    // Custom SVGs for premium feel
    if (type.includes("moto")) {
        return <path d="M18 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z M2 13h4l2 2 M22 13h-4l-2 2 M7 15l5-5 5 5 M12 13v2" stroke="white" strokeWidth="2" />;
    }
    if (type.includes("car")) return <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2 M7 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z M17 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" stroke="white" strokeWidth="2" />;
    if (type.includes("bici") || type.includes("bike")) return <path d="M15.5 13.5c1.4 0 2.5 1.1 2.5 2.5s-1.1 2.5-2.5 2.5-2.5-1.1-2.5-2.5 1.1-2.5 2.5-2.5zm-9 0c1.4 0 2.5 1.1 2.5 2.5S7.9 18.5 6.5 18.5 4 17.4 4 16s1.1-2.5 2.5-2.5zm4.5-5.5L13 13h3.5l1.5-2h-3.5L12 6M8 8l2 2 2-2M12 13H8.5L7 11h3.5l1.5 2z" stroke="white" strokeWidth="2" />;
    if (type.includes("patin") || type.includes("scooter")) return <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="white" />;
    return <path d="M12 2L2 22l10-4 10 4L12 2z" fill="white" />;
};

const createRiderIcon = (vehicle) => L.divIcon({
    className: 'custom-rider-marker',
    html: `<div class="rider-marker-pulsing">
            <div class="dot-container">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    ${renderToString(getVehicleIcon(vehicle))}
                </svg>
            </div>
            <div class="pulse"></div>
          </div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12]
});

export default function RiderDashboard() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [riderInfo, setRiderInfo] = useState(null);
    const [currentPos, setCurrentPos] = useState(null);
    const [mapStyle, setMapStyle] = useState("dark_all");
    const audioRef = React.useRef(new Audio("/sounds/conexo_alert.mp3"));

    useEffect(() => {
        // Lógica de Mapa Día/Noche: 7:00 AM a 7:30 PM es día
        const hour = new Date().getHours();
        if (hour >= 7 && hour < 19.5) {
            setMapStyle("light_all");
        } else {
            setMapStyle("dark_all");
        }
    }, []);

    const fetchJobs = async () => {
        try {
            const token = localStorage.getItem("rider_token");
            if (!token) return;
            
            // Obtener status del rider desde el backend
            const statusRes = await axios.get(`${API}/rider/status`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const res = await axios.get(`${API}/rider/jobs/assigned`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Si el número de pedidos asignados aumenta, sonar alerta
            if (res.data.length > jobs.length) {
                console.log("¡Nuevo pedido asignado! Sonando alerta...");
                audioRef.current.play().catch(e => console.log("Audio play failed (user interaction required):", e));
                toast.info("¡Tienes un nuevo pedido asignado!", {
                    icon: "🔔",
                    style: { background: "#10b981", color: "white", fontWeight: "bold" }
                });
            }

            setJobs(res.data);

            // Combinar info local con status del servidor
            const localUser = JSON.parse(localStorage.getItem("rider_data") || "{}");
            setRiderInfo({
                ...localUser,
                status: statusRes.data.status,
                completed_count: localUser.completed_count || 0
            });
        } catch (error) {
            // Silencio errores si es por falta de auth (login redirigirá) o si es error de red común
            if (error.response?.status !== 401 && error.code !== 'ERR_NETWORK') {
                console.error("Error fetching jobs", error);
                toast.error("Error al actualizar pedidos");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJobs();
        const interval = setInterval(fetchJobs, 15000); // Polling every 15s

        // Get location for the radar map
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition((pos) => {
                setCurrentPos([pos.coords.latitude, pos.coords.longitude]);
            });
        }

        return () => clearInterval(interval);
    }, [jobs.length]);

    const handleAction = async (jobId, actionPath) => {
        const token = localStorage.getItem("rider_token");
        try {
            await axios.post(`${API}/rider/jobs/${jobId}/${actionPath}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Estado actualizado con éxito");
            fetchJobs();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error al actualizar");
        }
    };

    const activeJob = jobs.length > 0 ? jobs[0] : null;

    return (
        <RiderLayout>
            <div className="flex flex-col min-h-full">
                <style dangerouslySetInnerHTML={{
                    __html: `
                    .rider-marker-pulsing {
                        position: relative;
                        width: 20px;
                        height: 20px;
                    }
                    .rider-marker-pulsing .dot-container {
                        width: 24px;
                        height: 24px;
                        background: #3b82f6;
                        border: 2px solid white;
                        border-radius: 50%;
                        position: absolute;
                        top: 0;
                        left: 0;
                        z-index: 2;
                        box-shadow: 0 0 15px rgba(59,130,246,0.8);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    .rider-marker-pulsing .pulse {
                        width: 32px;
                        height: 32px;
                        background: rgba(59, 130, 246, 0.4);
                        border-radius: 50%;
                        position: absolute;
                        top: -4px;
                        left: -4px;
                        z-index: 1;
                        animation: rider-pulse 2s infinite;
                    }
                    @keyframes rider-pulse {
                        0% { transform: scale(0.5); opacity: 1; }
                        100% { transform: scale(2.5); opacity: 0; }
                    }
                    /* Fuerza la desaparición de la atribución de Leaflet */
                    .leaflet-control-attribution {
                        display: none !important;
                    }
                `}} />

                {/* Header Stats */}
                {riderInfo && (
                    <div className="grid grid-cols-2 gap-3 p-4">
                        <div className="bg-white/5 border border-white/10 p-4 rounded-2xl flex flex-col items-center justify-center relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-16 h-16 bg-primary/10 blur-xl rounded-full" />
                            <span className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-1 z-10">Completados</span>
                            <span className="text-3xl font-black text-white z-10">{riderInfo.completed_count || 0}</span>
                        </div>
                        <button 
                            onClick={async () => {
                                const token = localStorage.getItem("rider_token");
                                const newStatus = riderInfo.status === 'online' ? false : true;
                                try {
                                    await axios.post(`${API}/rider/status`, { online: newStatus }, {
                                        headers: { Authorization: `Bearer ${token}` }
                                    });
                                    setRiderInfo({...riderInfo, status: newStatus ? 'online' : 'offline'});
                                    toast.success(newStatus ? "¡Estás online!" : "Te has desconectado");
                                } catch (error) {
                                    toast.error(error.response?.data?.detail || "Error al cambiar estado");
                                }
                            }}
                            className={`p-4 rounded-2xl flex flex-col items-center justify-center relative overflow-hidden transition-all active:scale-95 ${
                                riderInfo.status === 'online' 
                                    ? 'bg-green-500/20 border border-green-500/30' 
                                    : 'bg-red-500/20 border border-red-500/30'
                            }`}
                        >
                            <div className={`absolute top-0 left-0 w-16 h-16 blur-xl rounded-full ${
                                riderInfo.status === 'online' ? 'bg-green-500/20' : 'bg-red-500/20'
                            }`} />
                            <span className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-1 z-10">Estado</span>
                            <span className={`text-[10px] font-black uppercase tracking-widest z-10 px-3 py-1.5 rounded-full border ${
                                riderInfo.status === 'online' 
                                    ? 'text-green-400 bg-green-500/10 border-green-500/20' 
                                    : 'text-red-400 bg-red-500/10 border-red-500/20'
                            }`}>
                                {riderInfo.status === 'online' ? '● ONLINE' : '○ OFFLINE'}
                            </span>
                        </button>
                    </div>
                )}

                {/* Active Job Area */}
                <div className="flex-1">
                    <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-4 px-4 flex items-center gap-2">
                        <Navigation size={14} className="text-primary" />
                        Pedido en curso
                    </h2>

                    {loading ? (
                        <div className="h-48 flex items-center justify-center bg-white/5 border border-white/10 rounded-3xl animate-pulse">
                            <Truck size={32} className="text-slate-600 animate-bounce" />
                        </div>
                    ) : activeJob ? (
                        <div className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden shadow-2xl relative">
                            {/* Map Section */}
                            <div className="h-[55vh] w-full bg-slate-900 relative border-y border-white/5">
                                {activeJob.pickup_lat && activeJob.dropoff_lat ? (
                                    <MapContainer
                                        center={[(activeJob.pickup_lat + activeJob.dropoff_lat) / 2, (activeJob.pickup_lng + activeJob.dropoff_lng) / 2]}
                                        zoom={14}
                                        scrollWheelZoom={false}
                                        className="h-full w-full z-0"
                                        zoomControl={false}
                                        attributionControl={false}
                                    >
                                        <TileLayer url={`https://{s}.basemaps.cartocdn.com/${mapStyle}/{z}/{x}/{y}{r}.png`} attribution="" />
                                        <Marker position={[activeJob.pickup_lat, activeJob.pickup_lng]} icon={iconPickup}>
                                            <Popup>Recoger: {activeJob.restaurant_name}</Popup>
                                        </Marker>
                                        <Marker position={[activeJob.dropoff_lat, activeJob.dropoff_lng]} icon={iconDropoff}>
                                            <Popup>Entregar aquí</Popup>
                                        </Marker>
                                        {currentPos && (
                                            <Marker position={currentPos} icon={createRiderIcon(riderInfo?.vehicle)}>
                                                <Popup>Tú estás aquí</Popup>
                                            </Marker>
                                        )}
                                    </MapContainer>
                                ) : (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]">
                                        <MapPin size={24} className="mb-2 opacity-50" />
                                        <span className="text-xs font-black uppercase">Sin coordenadas exactas</span>
                                    </div>
                                )}

                                <div className="absolute top-2 left-2 z-[400]">
                                    <div className="bg-black/80 backdrop-blur border border-white/10 rounded-full px-3 py-1 text-[10px] font-black uppercase text-yellow-500 flex items-center gap-1 shadow-lg">
                                        <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
                                        {activeJob.status === 'assigned' ? 'A Recoger' : 'En Camino'}
                                    </div>
                                </div>

                                <div className="absolute top-2 right-2 z-[400] flex gap-2">
                                    <button
                                        onClick={() => {
                                            const lat = activeJob.status === 'assigned' ? activeJob.pickup_lat : activeJob.dropoff_lat;
                                            const lng = activeJob.status === 'assigned' ? activeJob.pickup_lng : activeJob.dropoff_lng;
                                            window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
                                        }}
                                        className="bg-primary hover:bg-primary/80 text-white flex items-center gap-2 px-3 py-1.5 rounded-full shadow-lg backdrop-blur-md transition-all active:scale-95 border border-white/20 text-[10px] font-black uppercase tracking-widest"
                                    >
                                        <Navigation size={12} className="fill-white" />
                                        Navegar
                                    </button>
                                </div>
                            </div>

                            {/* Info Section */}
                            <div className="p-5 space-y-4">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <span className="text-[10px] text-primary font-black uppercase tracking-widest">CONEXO</span>
                                        <h3 className="font-bold text-xl leading-none mt-1">Pedido #{activeJob.job_id.slice(-6).toUpperCase()}</h3>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-[10px] text-slate-400 font-black uppercase tracking-widest block">Distancia</span>
                                        <span className="font-bold text-white text-lg">{activeJob.distance_km.toFixed(1)} km</span>
                                    </div>
                                </div>

                                <div className="bg-black/50 border border-white/5 rounded-xl p-4 space-y-3">
                                    <div className="flex gap-3 items-start">
                                        <div className="mt-0.5"><div className="w-4 h-4 rounded-full border-2 border-orange-500 flex items-center justify-center"><div className="w-1.5 h-1.5 bg-orange-500 rounded-full" /></div></div>
                                        <div>
                                            <p className="text-xs font-black uppercase tracking-widest text-slate-400">Recogida</p>
                                            <p className="text-sm text-white font-medium line-clamp-1">{activeJob.pickup_address || "C/ Restaurante (ver mapa)"}</p>
                                        </div>
                                    </div>

                                    <div className="h-6 w-0.5 bg-white/10 ml-2" />

                                    <div className="flex gap-3 items-start">
                                        <div className="mt-0.5"><div className="w-4 h-4 rounded-full border-2 border-green-500" /></div>
                                        <div>
                                            <p className="text-xs font-black uppercase tracking-widest text-slate-400">Entrega</p>
                                            <p className="text-sm text-white font-medium line-clamp-1">{activeJob.dropoff_address || "Dirección de cliente"}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-2">
                                    {activeJob.status === "assigned" && (
                                        <button
                                            onClick={() => handleAction(activeJob.job_id, "picked-up")}
                                            className="w-full py-4 rounded-xl bg-orange-500 hover:bg-orange-600 text-black font-black tracking-widest uppercase transition-all shadow-[0_4px_20px_rgba(249,115,22,0.3)] active:scale-[0.98] flex items-center justify-center gap-2"
                                        >
                                            Ya tengo el pedido <ChevronRight size={18} />
                                        </button>
                                    )}
                                    {activeJob.status === "picked_up" && (
                                        <button
                                            onClick={() => handleAction(activeJob.job_id, "delivered")}
                                            className="w-full py-4 rounded-xl bg-green-500 hover:bg-green-600 text-black font-black tracking-widest uppercase transition-all shadow-[0_4px_20px_rgba(34,197,94,0.3)] active:scale-[0.98] flex items-center justify-center gap-2"
                                        >
                                            Pedido Entregado <Check size={18} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-black/40 border-y border-white/5 overflow-hidden shadow-2xl relative">
                            {/* Radar Map Section */}
                            <div className="h-[65vh] w-full bg-slate-900 relative">
                                {currentPos ? (
                                    <MapContainer
                                        center={currentPos}
                                        zoom={15}
                                        scrollWheelZoom={false}
                                        className="h-full w-full z-0"
                                        zoomControl={false}
                                        attributionControl={false}
                                    >
                                        <TileLayer url={`https://{s}.basemaps.cartocdn.com/${mapStyle}/{z}/{x}/{y}{r}.png`} attribution="" />
                                        <Marker position={currentPos} icon={createRiderIcon(riderInfo?.vehicle)} />
                                    </MapContainer>
                                ) : (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500">
                                        <div className="w-12 h-12 border-2 border-primary/20 border-t-primary rounded-full animate-spin mb-4" />
                                        <span className="text-[10px] font-black uppercase tracking-widest animate-pulse">Buscando señal GPS...</span>
                                    </div>
                                )}

                                {/* Overlay for idle state - Small corner badge */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none z-10" />

                                <div className="absolute top-4 left-4 z-20">
                                    <div className="bg-black/80 backdrop-blur-md border border-white/10 rounded-2xl p-3 shadow-xl">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                            <h3 className="text-xs font-black text-white uppercase tracking-widest">Escaneando zona</h3>
                                        </div>
                                        <p className="text-[9px] text-slate-400 font-bold uppercase">Buscando pedidos cerca...</p>
                                    </div>
                                </div>

                                <div className="absolute bottom-6 left-0 right-0 text-center z-20">
                                    <Link to="/rider/jobs" className="inline-block px-8 py-3 bg-primary hover:bg-blue-600 text-white rounded-full text-[11px] font-black uppercase tracking-[2px] shadow-[0_10px_25px_rgba(59,130,246,0.4)] transition-all transform active:scale-95">
                                        Ver Disponibles
                                    </Link>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Cola de Pedidos (si hay más de 1 asignado) */}
                {jobs.length > 1 && (
                    <div className="pt-4 space-y-3">
                        <span className="text-[10px] uppercase font-black tracking-widest text-slate-500 ml-1">En Cola ({jobs.length - 1} más)</span>
                        {jobs.slice(1).map(j => (
                            <div key={j.job_id} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex justify-between items-center opacity-70">
                                <div className="flex-1 overflow-hidden pr-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="w-2 h-2 rounded-full bg-slate-500" />
                                        <span className="text-xs font-bold text-white truncate">{j.restaurant_name}</span>
                                    </div>
                                    <p className="text-[10px] text-slate-400 truncate">{j.dropoff_address}</p>
                                </div>
                                <span className="text-xs font-black text-slate-300 bg-black/50 px-3 py-1.5 rounded-lg border border-white/5">
                                    {j.distance_km.toFixed(1)} km
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </RiderLayout>
    );
}
